// utils/jobMatching.js - Updated with Resource Matching

/**
 * Match user skills and resources to job requirements
 * @param {Object} userSkills - User's skill data from SkillContext
 * @param {Object} job - Job object with required and preferred skills
 * @param {Number} userTrustScore - User's trust score
 * @param {Object} userResources - User's resources from ResourceContext (NEW)
 * @returns {Object} Match result with score and details
 */
export const matchUserToJob = (userSkills, job, userTrustScore = 3.0, userResources = null) => {
    if (!userSkills || !job) {
      return {
        qualified: false,
        matchScore: 0,
        meetsAllRequirements: false,
        meetsTrustRequirement: false,
        missingSkills: [],
        matchingSkills: [],
        preferredSkillMatches: [],
        resourceMatch: null
      };
    }
  
    // Check trust requirement
    const meetsTrustRequirement = userTrustScore >= (job.minTrustScore || 3.0);
  
    // Get user's verified skill IDs
    const userVerifiedSkillIds = userSkills.verified.map(s => s.skillId || s.id);
  
    // Check required skills
    const requiredSkillResults = (job.requiredSkills || []).map(skill => {
      const hasSkill = userVerifiedSkillIds.includes(skill.id);
      return {
        skill,
        hasSkill
      };
    });
  
    const matchingRequiredSkills = requiredSkillResults.filter(r => r.hasSkill);
    const missingRequiredSkills = requiredSkillResults.filter(r => !r.hasSkill);
  
    // Check preferred skills
    const preferredSkillMatches = (job.preferredSkills || []).filter(skill =>
      userVerifiedSkillIds.includes(skill.id)
    );
  
    // Check resource requirements if present
    let resourceMatch = {
      hasRequirements: false,
      meetsAllRequirements: true,
      matching: [],
      missing: []
    };
    
    if (job.resourceRequirements && userResources) {
      resourceMatch.hasRequirements = Object.values(job.resourceRequirements).some(reqs => reqs && reqs.length > 0);
      
      if (resourceMatch.hasRequirements) {
        const resourceCheck = checkResourceRequirements(job.resourceRequirements, userResources);
        resourceMatch = { ...resourceMatch, ...resourceCheck };
      }
    }
  
    // Calculate match percentage
    const requiredSkillsCount = job.requiredSkills?.length || 0;
    const matchingRequiredCount = matchingRequiredSkills.length;
    const preferredSkillsCount = job.preferredSkills?.length || 0;
    const matchingPreferredCount = preferredSkillMatches.length;
  
    // Base score from required skills (0-60%)
    const requiredScore = requiredSkillsCount > 0
      ? (matchingRequiredCount / requiredSkillsCount) * 60
      : 60; // If no required skills, give full base score
  
    // Bonus from preferred skills (0-15%)
    const preferredScore = preferredSkillsCount > 0
      ? (matchingPreferredCount / preferredSkillsCount) * 15
      : 0;
  
    // Resource score (0-15%)
    const resourceScore = resourceMatch.hasRequirements
      ? (resourceMatch.meetsAllRequirements ? 15 : 0)
      : 15; // If no resource requirements, give full score
  
    // Trust score bonus (0-10%)
    const trustBonus = meetsTrustRequirement ? 10 : 0;
  
    const totalScore = Math.round(requiredScore + preferredScore + resourceScore + trustBonus);
  
    // Determine if qualified
    const meetsAllRequirements = matchingRequiredCount === requiredSkillsCount;
    const meetsResourceRequirements = !resourceMatch.hasRequirements || resourceMatch.meetsAllRequirements;
    const qualified = meetsAllRequirements && meetsTrustRequirement && meetsResourceRequirements;
  
    return {
      qualified,
      matchScore: totalScore,
      meetsAllRequirements,
      meetsTrustRequirement,
      meetsResourceRequirements,
      missingSkills: missingRequiredSkills.map(r => r.skill),
      matchingSkills: matchingRequiredSkills.map(r => r.skill),
      preferredSkillMatches,
      resourceMatch,
      requiredMatches: `${matchingRequiredCount}/${requiredSkillsCount}`,
      preferredMatches: `${matchingPreferredCount}/${preferredSkillsCount}`,
      details: {
        requiredScore,
        preferredScore,
        resourceScore,
        trustBonus
      }
    };
  };
  
  /**
   * Check if user resources meet job requirements
   * @param {Object} jobResourceRequirements - Required resources from job
   * @param {Object} userResources - User's available resources
   * @returns {Object} Resource match details
   */
  const checkResourceRequirements = (jobResourceRequirements, userResources) => {
    const matching = [];
    const missing = [];
    let meetsAllRequirements = true;
    
    Object.entries(jobResourceRequirements).forEach(([category, requirements]) => {
      if (!requirements || requirements.length === 0) return;
      
      const userResourcesInCategory = userResources[category] || [];
      
      requirements.forEach(req => {
        const matchingResource = userResourcesInCategory.find(resource => {
          // Check type match
          if (resource.type !== req.type) return false;
          
          // Check capacity if required
          if (req.minCapacity && resource.details.capacity < req.minCapacity) return false;
          
          // Check verification status
          if (resource.verification.status !== 'verified') return false;
          
          // Check availability (basic check for now)
          // TODO: Add more sophisticated availability checking with calendar
          
          return true;
        });
        
        if (matchingResource) {
          matching.push({ 
            requirement: req, 
            resource: matchingResource,
            category 
          });
        } else {
          missing.push({ 
            ...req, 
            category,
            reason: determineResourceMissingReason(req, userResourcesInCategory)
          });
          if (req.required !== false) {
            meetsAllRequirements = false;
          }
        }
      });
    });
    
    return { matching, missing, meetsAllRequirements };
  };
  
  /**
   * Determine why a resource requirement isn't met
   * @param {Object} requirement - The resource requirement
   * @param {Array} userResources - User's resources in this category
   * @returns {string} Reason for not meeting requirement
   */
  const determineResourceMissingReason = (requirement, userResources) => {
    const matchingType = userResources.filter(r => r.type === requirement.type);
    
    if (matchingType.length === 0) {
      return 'not_registered';
    }
    
    const unverified = matchingType.filter(r => r.verification.status !== 'verified');
    if (unverified.length > 0) {
      return 'not_verified';
    }
    
    const insufficientCapacity = matchingType.filter(r => 
      requirement.minCapacity && r.details.capacity < requirement.minCapacity
    );
    if (insufficientCapacity.length > 0) {
      return 'insufficient_capacity';
    }
    
    return 'not_available';
  };
  
  /**
   * Calculate job score for sorting
   * @param {Object} job - Job object
   * @param {Number} matchScore - Match score from matchUserToJob
   * @returns {Number} Sort score
   */
  export const calculateJobScore = (job, matchScore) => {
    // Factors for scoring:
    // 1. Match score (0-100)
    // 2. Token reward (normalized to 0-50)
    // 3. Urgency (based on deadline, 0-30)
    // 4. Platform job bonus (0-20)
    
    const tokenScore = Math.min(job.tokenReward / 2, 50);
    
    const deadline = new Date(job.schedule?.date);
    const now = new Date();
    const hoursUntilDeadline = (deadline - now) / (1000 * 60 * 60);
    const urgencyScore = hoursUntilDeadline < 24 ? 30 : 
                        hoursUntilDeadline < 48 ? 20 :
                        hoursUntilDeadline < 72 ? 10 : 0;
    
    const platformBonus = job.isPlatformJob ? 20 : 0;
    
    return matchScore + (tokenScore * 0.3) + (urgencyScore * 0.2) + platformBonus;
  };
  
  /**
   * Get skill gap analysis
   * @param {Object} userSkills - User's skills
   * @param {Array} targetJobs - Jobs user wants to qualify for
   * @returns {Object} Analysis of most valuable skills to acquire
   */
  export const getSkillGapAnalysis = (userSkills, targetJobs) => {
    const skillGaps = {};
    const userSkillIds = userSkills.verified.map(s => s.skillId || s.id);
  
    targetJobs.forEach(job => {
      job.requiredSkills.forEach(skill => {
        if (!userSkillIds.includes(skill.id)) {
          if (!skillGaps[skill.id]) {
            skillGaps[skill.id] = {
              skill,
              jobCount: 0,
              totalTokens: 0,
              jobs: []
            };
          }
          skillGaps[skill.id].jobCount++;
          skillGaps[skill.id].totalTokens += job.tokenReward;
          skillGaps[skill.id].jobs.push({
            id: job.id,
            title: job.title,
            tokenReward: job.tokenReward,
            isPlatformJob: job.isPlatformJob
          });
        }
      });
    });
  
    // Sort by potential value
    const sortedGaps = Object.values(skillGaps).sort((a, b) => {
      // Prioritize skills needed for platform jobs
      const aPlatformJobs = a.jobs.filter(j => j.isPlatformJob).length;
      const bPlatformJobs = b.jobs.filter(j => j.isPlatformJob).length;
      
      if (aPlatformJobs !== bPlatformJobs) {
        return bPlatformJobs - aPlatformJobs;
      }
      
      const aValue = a.jobCount * 10 + a.totalTokens;
      const bValue = b.jobCount * 10 + b.totalTokens;
      return bValue - aValue;
    });
  
    return sortedGaps;
  };
  
  /**
   * Get resource gap analysis
   * @param {Object} userResources - User's resources
   * @param {Array} targetJobs - Jobs user wants to qualify for
   * @returns {Object} Analysis of most valuable resources to acquire
   */
  export const getResourceGapAnalysis = (userResources, targetJobs) => {
    const resourceGaps = {};
    
    targetJobs.forEach(job => {
      if (!job.resourceRequirements) return;
      
      Object.entries(job.resourceRequirements).forEach(([category, requirements]) => {
        requirements.forEach(req => {
          const key = `${category}_${req.type}`;
          
          // Check if user has this resource
          const userHasResource = userResources[category]?.some(r => 
            r.type === req.type && 
            r.verification.status === 'verified' &&
            (!req.minCapacity || r.details.capacity >= req.minCapacity)
          );
          
          if (!userHasResource) {
            if (!resourceGaps[key]) {
              resourceGaps[key] = {
                category,
                type: req.type,
                typeName: req.typeName,
                jobCount: 0,
                totalTokens: 0,
                jobs: []
              };
            }
            resourceGaps[key].jobCount++;
            resourceGaps[key].totalTokens += job.tokenReward;
            resourceGaps[key].jobs.push({
              id: job.id,
              title: job.title,
              tokenReward: job.tokenReward
            });
          }
        });
      });
    });
    
    // Sort by potential value
    const sortedGaps = Object.values(resourceGaps).sort((a, b) => {
      const aValue = a.jobCount * 20 + a.totalTokens;
      const bValue = b.jobCount * 20 + b.totalTokens;
      return bValue - aValue;
    });
    
    return sortedGaps;
  };
  
  /**
   * Format job for display with resource information
   * @param {Object} job - Raw job object
   * @param {Object} matchResult - Result from matchUserToJob
   * @returns {Object} Formatted job for UI
   */
  export const formatJobForDisplay = (job, matchResult = {}) => {
    const scheduleDate = job.schedule?.date || job.schedule?.deadline;
    const deadline = scheduleDate ? new Date(scheduleDate) : null;
    const now = new Date();
    const hoursUntilDeadline = deadline && !isNaN(deadline) ? Math.floor((deadline - now) / (1000 * 60 * 60)) : null;
    
    let urgencyLevel = 'normal';
    let urgencyText = '';
    
    if (hoursUntilDeadline !== null) {
      if (hoursUntilDeadline < 6) {
        urgencyLevel = 'critical';
        urgencyText = `${hoursUntilDeadline} hours left!`;
      } else if (hoursUntilDeadline < 24) {
        urgencyLevel = 'urgent';
        urgencyText = 'Less than 24 hours';
      } else if (hoursUntilDeadline < 48) {
        urgencyLevel = 'moderate';
        urgencyText = `${Math.floor(hoursUntilDeadline / 24)} days left`;
      }
    }
  
    const formattedDeadline = deadline && !isNaN(deadline) 
      ? deadline.toLocaleDateString('en-US', {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
      : 'Flexible';

    return {
      ...job,
      ...matchResult,
      urgencyLevel,
      urgencyText,
      hoursUntilDeadline,
      formattedDeadline,
      estimatedEarnings: job.tokenReward,
      displayStatus: matchResult?.qualified ? 'qualified' : 
                    (matchResult?.matchScore || 0) >= 50 ? 'opportunity' : 
                    'not-qualified',
      isPlatformJob: job.isPlatformJob || false,
      hasResourceRequirements: matchResult?.resourceMatch?.hasRequirements || false
    };
  };